import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { toast } from "react-toastify";
import styles from "./WarmLeads.module.css";

const STATUS_FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "sent", label: "Sent" },
  { key: "replied", label: "Replied" },
  { key: "skipped", label: "Skipped" },
  { key: "all", label: "All" },
];

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}

const WarmLeads = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("pending");
  // Per-card local edits so the admin can tweak the draft before sending.
  // Keyed by lead id → { subject, body }.
  const [edits, setEdits] = useState({});
  const [busyIds, setBusyIds] = useState(new Set());
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugError, setDebugError] = useState("");

  const fetchData = async (statusOverride) => {
    setLoading(true);
    try {
      const status = statusOverride ?? filter;
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/warm_leads?status=${encodeURIComponent(status)}&limit=200`,
        { credentials: "include" },
      );
      const json = await res.json();
      if (json?.status === 200) {
        setData(json.data);
      } else {
        toast.error(json?.message || "Failed to load warm leads");
      }
    } catch (err) {
      console.error("[warm_leads]", err);
      toast.error("Failed to load warm leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const items = data?.items || [];
  const summary = data?.summary || {};

  const markBusy = (id, busy) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const runExtractor = async () => {
    setRunning(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/warm_leads/run`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        },
      );
      const json = await res.json();
      if (json?.status === 200) {
        const r = json.data;
        // Full counter line so we can see where the pipeline dropped rows.
        // 0 raw_engaged → Zoho recent-campaigns endpoint returned nothing
        // raw_engaged → after_warm_threshold drop → most engagers didn't
        //   hit opens≥WARM_OPENS_THRESHOLD or clicks≥WARM_CLICKS_THRESHOLD
        // after_warm_threshold → after_user_dedup drop → existing Users
        // after_user_dedup → after_noise drop → personal mailboxes
        toast.success(
          `Campaigns ${r.campaigns_scanned ?? "?"}/${r.campaigns_total ?? "?"} · New ${r.new_leads} · Refreshed ${r.refreshed_leads} · Skipped ${r.skipped_existing_sent} · Failed ${r.failed} · Raw ${r.raw_engaged} → Threshold ${r.after_warm_threshold ?? "?"} → Dedup ${r.after_user_dedup} → Noise ${r.after_noise_filter}`,
          { autoClose: 14000 },
        );
        await fetchData();
      } else {
        toast.error(json?.message || "Extraction failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Extraction failed");
    } finally {
      setRunning(false);
    }
  };

  const sendLead = async (lead) => {
    markBusy(lead.id, true);
    try {
      const edit = edits[lead.id] || {};
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/warm_leads/${lead.id}/send`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            subject: edit.subject ?? lead.draft_subject,
            body: edit.body ?? lead.draft_body,
          }),
        },
      );
      const json = await res.json();
      if (json?.status === 200) {
        toast.success(`Sent to ${lead.email}`);
        await fetchData();
      } else {
        toast.error(json?.message || "Send failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Send failed");
    } finally {
      markBusy(lead.id, false);
    }
  };

  const skipLead = async (lead) => {
    markBusy(lead.id, true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/warm_leads/${lead.id}/skip`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        },
      );
      const json = await res.json();
      if (json?.status === 200) {
        toast.success("Skipped");
        await fetchData();
      } else {
        toast.error(json?.message || "Skip failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Skip failed");
    } finally {
      markBusy(lead.id, false);
    }
  };

  const markReplied = async (lead) => {
    markBusy(lead.id, true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/warm_leads/${lead.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ status: "replied" }),
        },
      );
      const json = await res.json();
      if (json?.status === 200) {
        toast.success("Marked as replied");
        await fetchData();
      } else {
        toast.error(json?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      markBusy(lead.id, false);
    }
  };

  const runDebug = async () => {
    setDebugLoading(true);
    setDebugError("");
    setDebugData(null);
    setDebugOpen(true);
    try {
      // Calls the BACKEND /admin/warm_leads/debug via the same base
      // the rest of this page uses — avoids the "hit the FE route by
      // mistake" issue (which returns the SPA shell HTML).
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/warm_leads/debug`,
        { credentials: "include" },
      );
      const text = await res.text();
      try {
        const json = JSON.parse(text);
        setDebugData(json);
      } catch {
        setDebugError(
          `Expected JSON but got ${res.status} response (preview): ${text.slice(0, 400)}`,
        );
      }
    } catch (err) {
      setDebugError(String(err?.message || err));
    } finally {
      setDebugLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Warm Leads</h1>
          <div className={styles.subtitle}>
            Newsletter recipients who engaged but never signed up. The weekly
            extractor pulls them, drafts a personal founder note via Claude,
            and pushes them to Zoho CRM. Review, tweak, and send.
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            onClick={() => fetchData()}
            startIcon={<RefreshOutlinedIcon />}
            sx={{ textTransform: "none", color: "#607084" }}
          >
            Refresh
          </Button>
          <Button
            onClick={runDebug}
            sx={{ textTransform: "none", color: "#607084" }}
          >
            Debug Zoho
          </Button>
          <Button
            className={styles.runBtn}
            onClick={runExtractor}
            disabled={running}
            startIcon={
              running ? (
                <CircularProgress size={16} sx={{ color: "#ffffff" }} />
              ) : (
                <PlayCircleOutlineIcon />
              )
            }
          >
            {running ? "Running…" : "Run extractor now"}
          </Button>
        </div>
      </div>

      <div className={styles.chipRow}>
        {STATUS_FILTERS.map((s) => {
          const count = s.key === "all"
            ? Object.values(summary || {}).reduce((sum, n) => sum + Number(n || 0), 0)
            : Number(summary?.[s.key] || 0);
          return (
            <span
              key={s.key}
              className={`${styles.chip} ${filter === s.key ? styles.chipActive : ""}`}
              onClick={() => setFilter(s.key)}
            >
              {s.label}
              <span className={styles.chipCount}>{count}</span>
            </span>
          );
        })}
      </div>

      {loading && !data ? (
        <div className={styles.loaderWrap}>
          <CircularProgress />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>
          No warm leads in this view. The extractor runs every Monday — or hit
          “Run extractor now” to fire it on demand.
        </div>
      ) : (
        <div className={styles.list}>
          {items.map((lead) => {
            const edit = edits[lead.id] || {};
            const subjectVal = edit.subject ?? lead.draft_subject ?? "";
            const bodyVal = edit.body ?? lead.draft_body ?? "";
            const isBusy = busyIds.has(lead.id);
            const isPending = lead.status === "pending";

            return (
              <div key={lead.id} className={styles.card}>
                <div className={styles.who}>
                  <strong>{lead.name || lead.company || lead.email}</strong>
                  <span className={styles.meta}>
                    {lead.email}
                    {lead.company ? ` · ${lead.company}` : ""}
                    {lead.last_campaign_name
                      ? ` · from "${lead.last_campaign_name}"`
                      : ""}
                  </span>
                  <span className={styles.meta}>
                    Last engaged: {formatDate(lead.last_engagement_at)}
                  </span>
                  <div className={styles.engagement}>
                    <span className={styles.pill}>Opens · {lead.opens || 0}</span>
                    <span className={`${styles.pill} ${styles.pillClicks}`}>
                      Clicks · {lead.clicks || 0}
                    </span>
                    {lead.zoho_lead_id ? (
                      <span className={styles.pill}>In Zoho CRM</span>
                    ) : null}
                  </div>
                  {lead.status === "sent" && (
                    <>
                      <div style={{ marginTop: 10 }}>
                        <span className={`${styles.sentBadge}`}>Sent</span>
                      </div>
                      <div className={styles.sentMeta}>
                        {formatDate(lead.sent_at)}
                        {lead.sent_by ? ` · by ${lead.sent_by}` : ""}
                      </div>
                    </>
                  )}
                  {lead.status === "replied" && (
                    <div style={{ marginTop: 10 }}>
                      <span
                        className={`${styles.sentBadge} ${styles.repliedBadge}`}
                      >
                        Replied
                      </span>
                    </div>
                  )}
                  {lead.status === "skipped" && (
                    <div style={{ marginTop: 10 }}>
                      <span
                        className={`${styles.sentBadge} ${styles.skippedBadge}`}
                      >
                        Skipped
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles.draft}>
                  <TextField
                    label="Subject"
                    fullWidth
                    size="small"
                    className={styles.subjectInput}
                    value={subjectVal}
                    onChange={(e) =>
                      setEdits((prev) => ({
                        ...prev,
                        [lead.id]: {
                          ...(prev[lead.id] || {}),
                          subject: e.target.value,
                        },
                      }))
                    }
                    disabled={!isPending}
                  />
                  <TextField
                    label="Body"
                    fullWidth
                    multiline
                    minRows={6}
                    className={styles.bodyArea}
                    value={bodyVal}
                    onChange={(e) =>
                      setEdits((prev) => ({
                        ...prev,
                        [lead.id]: {
                          ...(prev[lead.id] || {}),
                          body: e.target.value,
                        },
                      }))
                    }
                    disabled={!isPending}
                  />
                </div>

                <div className={styles.actions}>
                  {isPending ? (
                    <>
                      <Button
                        className={styles.sendBtn}
                        startIcon={
                          isBusy ? (
                            <CircularProgress size={14} sx={{ color: "#fff" }} />
                          ) : (
                            <SendOutlinedIcon />
                          )
                        }
                        disabled={isBusy}
                        onClick={() => sendLead(lead)}
                      >
                        {isBusy ? "Sending…" : "Send"}
                      </Button>
                      <Button
                        className={styles.skipBtn}
                        disabled={isBusy}
                        onClick={() => skipLead(lead)}
                      >
                        Skip
                      </Button>
                    </>
                  ) : lead.status === "sent" ? (
                    <Button
                      className={styles.repliedBtn}
                      disabled={isBusy}
                      onClick={() => markReplied(lead)}
                    >
                      Mark replied
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {debugOpen && (
        <div
          onClick={() => setDebugOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,27,44,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 900,
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              padding: 22,
              fontFamily: "Inter, system-ui, sans-serif",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16, color: "#1f2c3c" }}>
                Zoho debug — raw responses
              </h3>
              <Button onClick={() => setDebugOpen(false)} size="small">
                Close
              </Button>
            </div>
            {debugLoading && <div>Loading…</div>}
            {debugError && (
              <pre
                style={{
                  background: "#fdecea",
                  color: "#c0392b",
                  padding: 12,
                  borderRadius: 8,
                  whiteSpace: "pre-wrap",
                  fontSize: 12.5,
                }}
              >
                {debugError}
              </pre>
            )}
            {debugData && (
              <pre
                style={{
                  background: "#f7fbff",
                  padding: 14,
                  borderRadius: 10,
                  border: "1px solid #e6eff7",
                  fontSize: 12,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {JSON.stringify(debugData, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WarmLeads;
